// Pure ordering helpers for the comparison table's "rating vs global average"
// sort column (issue #148).
//
// The deviation value itself is NOT computed here — it is produced by
// domain/contrast.js (RegionContrast.ratingDeviation = a storefront's
// averageUserRating minus the cross-region mean, rounded to 2 dp, or null when
// fewer than two storefronts carry a rating). These helpers only define how
// rows ORDER by that already-computed deviation, so the table can surface the
// storefronts that diverge most from the global average.
//
// Two variants:
//   - 'signed':   order by the raw signed deviation (+ above avg, − below avg).
//                 Descending floats the strongest over-performers to the top;
//                 ascending floats the strongest under-performers to the top.
//   - 'absolute': order by |deviation|, so the largest divergences in EITHER
//                 direction cluster together regardless of sign.
//
// Rows with no comparable deviation (null/undefined — e.g. unavailable rows or
// a single-rating set where a mean is meaningless) always sort last, regardless
// of direction, mirroring the null-last rule used elsewhere in the table.

/** @typedef {'signed'|'absolute'} DeviationVariant */

export const DEVIATION_VARIANTS = Object.freeze({
  SIGNED: 'signed',
  ABSOLUTE: 'absolute',
});

/**
 * Comparator value for a single deviation under the given variant. Returns null
 * for a missing / non-finite deviation so callers can sort those rows last.
 *
 * @param {number|null|undefined} deviation - signed deviation from contrast.js.
 * @param {DeviationVariant} [variant='signed']
 * @returns {number|null} the orderable value, or null when there is no deviation.
 */
export function deviationSortValue(deviation, variant = DEVIATION_VARIANTS.SIGNED) {
  if (typeof deviation !== 'number' || !Number.isFinite(deviation)) return null;
  return variant === DEVIATION_VARIANTS.ABSOLUTE ? Math.abs(deviation) : deviation;
}

/**
 * Order a list by each item's rating deviation. Pure: returns a new array and
 * never mutates the input. Items whose deviation is null/undefined always sort
 * last, regardless of direction. Stable for items that compare equal.
 *
 * @template T
 * @param {T[]} items
 * @param {(item: T) => number|null|undefined} getDeviation - deviation accessor.
 * @param {object} [opts]
 * @param {DeviationVariant} [opts.variant='signed']
 * @param {'asc'|'desc'} [opts.dir='desc']
 * @returns {T[]} a new, ordered array.
 */
export function orderByDeviation(
  items,
  getDeviation,
  { variant = DEVIATION_VARIANTS.SIGNED, dir = 'desc' } = {},
) {
  const factor = dir === 'asc' ? 1 : -1;
  const list = Array.isArray(items) ? items.slice() : [];
  return list.sort((a, b) => {
    const av = deviationSortValue(getDeviation(a), variant);
    const bv = deviationSortValue(getDeviation(b), variant);
    // Nulls always sort last, regardless of direction.
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    return (av - bv) * factor;
  });
}
